import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { API_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE_URL = `${API_BASE_URL}/api/v1/rate-limit-events`;

export default function useRateLimitEvents() {
    const authToken = sessionStorage.getItem("authToken");
    const [events, setEvents] = useState([]);
    const [initialLoad, setInitialLoad] = useState(true);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);

    const handleAxiosErrors = (err, action = "operation") => {
        let message = "An unexpected error occurred.";
        if (err.response) {
            message =
                err.response.data?.message ||
                `Failed to ${action}: ${err.response.statusText}`;
        } else if (err.request) {
            message =
                "No response from the server. Please check your internet connection.";
        } else {
            message = err.message || "Something went wrong.";
        }
        toast.error(message);
        console.error(`${action} error:`, err);
    };

    const fetchAllEvents = useCallback(async (filters = {}) => {
        if (!authToken) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();

            // pagination fix (Spring = 0-based)
            params.append("page", pagination.current - 1);
            params.append("size", pagination.pageSize);

            if (filters.clientId) params.append("clientId", filters.clientId);
            if (filters.apiKeyId) params.append("apiKeyId", filters.apiKeyId);
            if (filters.outcome) params.append("outcome", filters.outcome);
            if (filters.type) params.append("type", filters.type);
            if (filters.path) params.append("path", filters.path);

            if (filters.from) params.append("from", filters.from);
            if (filters.to) params.append("to", filters.to);

            const res = await apiClient.get(`${BASE_URL}?${params.toString()}`);

            setEvents(res.data.content || []);
            setTotalPages(res.data.totalPages || 1);
            setTotalElements(res.data.totalElements || 0);
        } catch (error) {
            handleAxiosErrors(error, "fetch rate limit events");
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, [authToken, pagination.current, pagination.pageSize]);


    const updatePagination = (newPagination) => {
        setPagination((prev) => ({
            ...prev,
            ...newPagination,
        }));
    };

    useEffect(() => {
        if (authToken) {
            fetchAllEvents({});
        }
    }, [authToken, pagination.current, pagination.pageSize]);

    // polling
    useEffect(() => {

        const interval = setInterval(() => {
            fetchAllEvents();
        }, 60000);

        return () => clearInterval(interval);

    }, [fetchAllEvents]);

    return {
        events,
        loading,
        initialLoad,
        fetchAllEvents,
        totalPages,
        totalElements,
        pagination,
        setPagination: updatePagination,
    };
}
